import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalComponent from "..";
import { useAdminGetEventGuestQuery } from "../../../redux/features/admin/event/events.api";
import {
  useCheckGuestInMutation,
  useGetEventGuestQuery,
} from "../../../redux/features/event/event.api";
import { GetEventGuestResponse } from "../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { PRIMARY_COLOR, PRODUCT_PRICING } from "../../../utils/constants";
import {
  formatDate,
  formatToCurrency,
  getCurrencyShort,
} from "../../../utils/helpers";
import { EventPermissions, ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { GuestCard } from "../../cards/guestCard";
import { ProfileIcon } from "../../cards/profilePic";
import { EmptyState } from "../../empty";
import { Icon } from "../../icon/icon";
import { LoadingOval } from "../../loaders/loaders";
import { Pill } from "../../pill";
import { TableComponent } from "../../table";
import { MiniHeader } from "../../typography/miniHeader";

export const ViewGuestModal = ({
  open,
  handleClose,
  guestId,
  adminView = false,
  eventCurrency,
  userEventPermissions,
}: ModalCompProps & {
  guestId: string;
  adminView?: boolean;
  eventCurrency: PayoutCurrency;
  userEventPermissions: EventPermissions[];
}) => {
  const [guest, setGuest] = useState<GetEventGuestResponse>();
  const [fetchingGuestDetails, setFetchingGuestDetails] =
    useState<boolean>(false);

  const {
    data: clientGuest,
    isFetching: clientFetchingGuestDetails,
    refetch,
    isSuccess: guestDetailsSuccess,
  } = useGetEventGuestQuery(
    {
      id: guestId,
    },
    { refetchOnMountOrArgChange: true, skip: guestId === "" || adminView },
  );

  const [handleGuestCheckIn, { isLoading: checkingGuestIn }] =
    useCheckGuestInMutation();

  const navigate = useNavigate();

  const {
    data: adminGuest,
    isFetching: adminFetchingGuestDetails,
    isSuccess: adminnGuestDetailsSuccess,
  } = useAdminGetEventGuestQuery(
    {
      id: guestId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: guestId === "" || !adminView,
    },
  );

  useEffect(() => {
    if (adminView) {
      setFetchingGuestDetails(adminFetchingGuestDetails);
      setGuest(adminGuest);
    } else {
      setFetchingGuestDetails(clientFetchingGuestDetails);
      setGuest(clientGuest);
    }
  }, [
    clientGuest,
    clientFetchingGuestDetails,
    adminGuest,
    adminFetchingGuestDetails,
    adminView,
  ]);

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      containerClassName="md:!py-[20px] md:!px-[20px] md:!justify-end"
      className="!max-h-[90%] !rounded-t-[16px] !bg-white !p-[20px] md:!max-h-[100%] md:!max-w-[500px] md:!rounded-[16px]"
      dialogClassName="!my-0"
      showHeader
      mobileAnchorBottom
      closeOnOverlayClick
      fullScreen
      showCloseIcon
      showMobileTopBar={false}
    >
      {fetchingGuestDetails ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingOval color={PRIMARY_COLOR} />
        </div>
      ) : (
        <>
          {(!adminView && guestDetailsSuccess) ||
          (adminView && adminnGuestDetailsSuccess) ? (
            <div className="flex w-full flex-col gap-8">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <ProfileIcon dimension={64} img="" className="!p-[12px]" />

                <div>
                  <p className="text-base font-bold tracking-tight text-grey-900 md:text-2xl">
                    {guest?.data?.firstName} {guest?.data?.lastName}
                  </p>
                  <p className="text-sm font-bold text-grey-500 md:text-base">
                    {guest?.data?.email}
                  </p>
                </div>

                {guest?.data?.status === "checked-in" ? (
                  <p className="text-grey-500">
                    Checked in on{" "}
                    {formatDate(
                      guest?.data?.updatedAt,
                      "DD MMM, YYYY • h:mm A",
                    )}
                  </p>
                ) : undefined}

                {!adminView &&
                userEventPermissions.includes("checkInGuests") ? (
                  <>
                    {guest?.data?.status === "unchecked-in" ? (
                      <Button
                        type="button"
                        onClick={async () => {
                          const resp = await handleGuestCheckIn({
                            id: guestId,
                          });

                          if (resp.data?.status === "success") {
                            refetch();
                          }
                        }}
                        text="Check In Guests"
                        icon="calendar-check-2"
                        variation="plain-grey"
                        className="!max-w-[272px]"
                        disabled={checkingGuestIn}
                        loading={checkingGuestIn}
                      />
                    ) : (
                      <Button
                        type="button"
                        onClick={() => {}}
                        text="Checked In"
                        icon="circle-check"
                        variation="green-outline"
                        className="!max-w-[272px] hover:!scale-100"
                      />
                    )}
                  </>
                ) : undefined}

                {adminView && (
                  <Button
                    type="button"
                    onClick={() => {
                      navigate(`/users/${guest?.data?.userId}`);
                    }}
                    text="View User"
                    variation="plain-grey"
                    className="!max-w-[272px] hover:!scale-100"
                  />
                )}
              </div>

              <TableComponent
                headers={[]}
                rows={[
                  {
                    id: 1,
                    items: [
                      <div className="flex flex-col gap-[2px]">
                        <div className="flex items-center justify-between gap-2 font-bold text-primary">
                          <p>
                            {guest?.data?.tickets?.reduce((acc, el) => {
                              return acc + el.quantity;
                            }, 0)}{" "}
                            {guest?.data?.tickets?.reduce((acc, el) => {
                              return acc + el.quantity;
                            }, 0) === 1
                              ? "Ticket"
                              : "Tickets"}
                          </p>

                          <p className="text-base font-bold">
                            {formatToCurrency(
                              guest?.data?.payment?.amount ?? 0,
                              { currency: getCurrencyShort(eventCurrency) },
                            )}
                          </p>
                        </div>

                        <div className="flex items-start gap-2">
                          <div className="flex flex-1 flex-wrap items-center gap-2">
                            {guest?.data?.tickets?.map((t, idx) => {
                              return (
                                <Pill
                                  text={`${t.quantity}X ${t?.ticketId?.name}`}
                                  variation="grey"
                                  key={`${idx}-ticket`}
                                />
                              );
                            })}
                          </div>

                          {guest?.data?.discount && (
                            <p className="text-base text-grey-500 line-through">
                              {formatToCurrency(
                                guest?.data?.discount
                                  ? guest?.data?.discount?.type === "amount"
                                    ? (guest?.data?.payment?.amount ?? 0) +
                                      (guest?.data?.discount?.value ?? 0)
                                    : (guest?.data?.payment?.amount ?? 0) /
                                      ((guest?.data?.discount?.value ?? 0) /
                                        100)
                                  : (guest?.data?.payment?.amount ?? 0),
                                { currency: getCurrencyShort(eventCurrency) },
                              )}
                            </p>
                          )}
                        </div>
                      </div>,
                    ],
                  },
                  {
                    id: 2,
                    items: [
                      guest?.data?.discount ? (
                        <div className="flex items-center gap-2">
                          <Icon
                            svg="ticket-percent"
                            width={24}
                            height={24}
                            className="text-blue"
                          />

                          <p className="flex-1 text-base font-bold uppercase text-primary">
                            {guest?.data?.discount?.name}
                          </p>

                          <p className="text-base text-grey-500">
                            {guest?.data?.discount?.type === "amount"
                              ? `${formatToCurrency(
                                  guest?.data?.discount?.value,
                                  { currency: getCurrencyShort(eventCurrency) },
                                )} off`
                              : ""}
                            {guest?.data?.discount?.type === "percentage"
                              ? `${guest?.data?.discount?.value}% off`
                              : ""}
                          </p>
                        </div>
                      ) : undefined,
                    ],
                  },
                  {
                    id: 3,
                    items: [
                      <div className="flex flex-col gap-[2px]">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-grey-500">Our Fee</p>

                          <Pill
                            text={
                              guest?.data?.payment?.feeDescription ??
                              PRODUCT_PRICING[eventCurrency].feeText
                            }
                            variation="grey"
                          />
                        </div>

                        <p className="font-bold text-primary">
                          {formatToCurrency(guest?.data?.payment?.fees ?? 0, {
                            currency: getCurrencyShort(eventCurrency),
                          })}
                        </p>
                      </div>,
                    ],
                  },
                  {
                    id: 4,
                    items: [
                      <div>
                        <p className="text-base text-grey-500">
                          Date Registered
                        </p>
                        <p className="text-base font-bold text-primary">
                          {formatDate(
                            guest?.data.createdAt,
                            "MMMM DD, YYYY • h:mm A",
                          )}
                        </p>
                      </div>,
                    ],
                  },
                ]}
                onRowClick={(idx) => {}}
                isRowClickable={false}
                isTableEmpty={false}
              />

              {guest !== undefined &&
              ![0, undefined].includes(guest?.data?.invitedGuests?.length) ? (
                <div className="flex flex-col gap-2">
                  <MiniHeader header="Additional Guests" />

                  <TableComponent
                    headers={[]}
                    rows={guest.data.invitedGuests.map((invitedGuest, idx) => {
                      return {
                        id: idx,
                        items: [
                          <GuestCard
                            className=""
                            name={`${invitedGuest.firstName} ${invitedGuest.lastName}`}
                            email={`${invitedGuest.email}`}
                            timeAgo={undefined}
                            showTicket={false}
                            key={`invited-guests-row-${idx}`}
                          />,
                        ],
                      };
                    })}
                    onRowClick={(idx) => {}}
                    isRowClickable={false}
                    isTableEmpty={false}
                  />
                </div>
              ) : undefined}
            </div>
          ) : (
            <EmptyState
              text="Guest not found"
              image="/assets/images/guest-glass.png"
            />
          )}
        </>
      )}
    </ModalComponent>
  );
};
